/**
 * The map's affine view: a scale in pixels per layout unit and a centre.
 *
 * VOSviewer calls the scale a *scaling factor*, and it is not decoration: the
 * label-placement rule in `../model/labels.ts` compares a per-item threshold
 * against exactly this number, so the units have to agree.  Everything here is
 * in CSS pixels; device-pixel-ratio scaling is applied once, on the canvas
 * transform, and never leaks into the model.
 */

/** `[xMin, yMin, xMax, yMax]`. */
export type Bounds = readonly [number, number, number, number];

export class Viewport {
  /** Pixels per layout unit. */
  scale = 1;

  /** Layout coordinate drawn at the centre of the canvas. */
  centreX = 0;

  centreY = 0;

  /** Canvas size in CSS pixels. */
  width = 1;

  height = 1;

  /** Zoom limits, recomputed by `fitTo` so they follow the data's own extent. */
  minScale = 1e-6;

  maxScale = 1e6;

  setSize(width: number, height: number): void {
    this.width = Math.max(1, width);
    this.height = Math.max(1, height);
  }

  toScreenX(x: number): number {
    return this.width / 2 + (x - this.centreX) * this.scale;
  }

  toScreenY(y: number): number {
    // Layout y grows upwards, screen y grows downwards.
    return this.height / 2 - (y - this.centreY) * this.scale;
  }

  toWorldX(px: number): number {
    return this.centreX + (px - this.width / 2) / this.scale;
  }

  toWorldY(py: number): number {
    return this.centreY - (py - this.height / 2) / this.scale;
  }

  /** The visible world rectangle. */
  visibleBounds(): Bounds {
    const halfW = this.width / 2 / this.scale;
    const halfH = this.height / 2 / this.scale;
    return [this.centreX - halfW, this.centreY - halfH, this.centreX + halfW, this.centreY + halfH];
  }

  /**
   * Frame `bounds` with a margin of `padding` (a fraction of the larger side).
   *
   * The zoom limits are set from the fitted scale: you can pull back to a
   * quarter of it — enough to see the map is finished — and push in 400x,
   * which at a typical map extent is close enough to read a single label.
   */
  fitTo(bounds: Bounds, padding = 0.05): void {
    const [xMin, yMin, xMax, yMax] = bounds;
    const spanX = Math.max(xMax - xMin, 1e-9);
    const spanY = Math.max(yMax - yMin, 1e-9);
    const margin = 1 + 2 * Math.max(0, padding);
    const scale = Math.min(this.width / (spanX * margin), this.height / (spanY * margin));
    this.minScale = scale / 4;
    this.maxScale = scale * 400;
    this.scale = scale;
    this.centreX = (xMin + xMax) / 2;
    this.centreY = (yMin + yMax) / 2;
  }

  /**
   * Multiply the scale, keeping the world point under `anchor` (CSS pixels,
   * canvas-relative) pinned to the same pixel.  Without the anchor the centre
   * is used, which is what a keyboard zoom wants.
   */
  zoomBy(factor: number, anchor?: readonly [number, number]): void {
    if (!Number.isFinite(factor) || factor <= 0) return;
    const next = clamp(this.scale * factor, this.minScale, this.maxScale);
    if (next === this.scale) return;
    if (anchor) {
      const wx = this.toWorldX(anchor[0]);
      const wy = this.toWorldY(anchor[1]);
      const applied = next / this.scale;
      this.scale = next;
      // Solve for the centre that leaves (wx, wy) under the same pixel.
      this.centreX = wx + (this.centreX - wx) / applied;
      this.centreY = wy + (this.centreY - wy) / applied;
    } else {
      this.scale = next;
    }
  }

  /** Pan by a screen delta, in CSS pixels. */
  panByPixels(dx: number, dy: number): void {
    this.centreX -= dx / this.scale;
    this.centreY += dy / this.scale;
  }

  copyFrom(other: Viewport): void {
    this.scale = other.scale;
    this.centreX = other.centreX;
    this.centreY = other.centreY;
    this.width = other.width;
    this.height = other.height;
    this.minScale = other.minScale;
    this.maxScale = other.maxScale;
  }

  equals(other: Viewport): boolean {
    return (
      this.scale === other.scale &&
      this.centreX === other.centreX &&
      this.centreY === other.centreY &&
      this.width === other.width &&
      this.height === other.height
    );
  }
}

export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
